import axios, { getTenantSessionScope } from '../api/api1';

const inFlightRequests = new Map();
const recentResponses = new Map();
const DUPLICATE_WINDOW_MS = 3000;
let serviceCacheGeneration = 0;

/**
 * Prevent duplicate GETs caused by component mount/remount cycles.
 * This is intentionally a very short cache: it collapses accidental
 * duplicate requests without turning normal page navigation into stale data.
 */
async function deduplicateRequest(key, requestFn) {
  const scopedKey = `${getTenantSessionScope()}:${key}`;
  const cacheGeneration = serviceCacheGeneration;
  const cached = recentResponses.get(scopedKey);
  if (cached && (Date.now() - cached.timestamp) < DUPLICATE_WINDOW_MS) {
    return cached.data;
  }

  if (inFlightRequests.has(scopedKey)) {
    return inFlightRequests.get(scopedKey);
  }

  const promise = Promise.resolve().then(requestFn).then(data => {
    if (cacheGeneration === serviceCacheGeneration) {
      recentResponses.set(scopedKey, { timestamp: Date.now(), data });
    }
    return data;
  });

  inFlightRequests.set(scopedKey, promise);

  promise.then(
    () => { if (inFlightRequests.get(scopedKey) === promise) inFlightRequests.delete(scopedKey); },
    () => { if (inFlightRequests.get(scopedKey) === promise) inFlightRequests.delete(scopedKey); }
  );

  return promise;
}

export const clearServiceApiCache = () => {
  serviceCacheGeneration += 1;
  recentResponses.clear();
  inFlightRequests.clear();
};

const scopedCacheKey = (key) => `${getTenantSessionScope()}:${key}`;
export const requestStudentRenewal = async (studentId, planId = null) => {
  try {
    const payload = {};
    if (planId) payload.plan_id = Number(planId);
    const response = await axios.post(`/api/students/${studentId}/renewal-request`, payload);
    return response.data;
  } catch (error) {
    console.error('Error requesting student renewal:', error);
    throw error?.response?.data || error;
  }
};

export const getStudents = async (roomId = null, libraryId = null) => {
  return deduplicateRequest(`getStudents-${roomId}-${libraryId}`, async () => {
    try {
      const params = {};
      if (roomId) params.room_id = roomId;
      if (libraryId) params.library_id = libraryId;

      const response = await axios.get('/api/students', { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching students:", error);
      throw error;
    }
  });
};

export const getRooms = async () => {
  return deduplicateRequest('getRooms', async () => {
    try {
      const response = await axios.get('/api/rooms');
      return response.data;
    } catch (error) {
      console.error("Error fetching rooms:", error);
      throw error;
    }
  });
};

export const getDashboardData = async (libraryId = null, forceRefresh = false) => {
  const requestKey = `getDashboardData-${libraryId}`;
  if (forceRefresh) {
    recentResponses.delete(scopedCacheKey(requestKey));
  }
  return deduplicateRequest(requestKey, async () => {
    try {
      const dashboardParams = {};
      if (libraryId) dashboardParams.library_id = libraryId;
      if (forceRefresh) dashboardParams._t = Date.now();

      // Use optimized consolidated endpoint
      const dashboardRes = await axios.get('/api/dashboard/complete', { params: dashboardParams, skipCache: forceRefresh });
      
      return {
        dashboard: dashboardRes.data,
        // Legacy compatibility - extract nested data
        payments: [],
        seats: [],
        rooms: [],
      };
    } catch (error) {
      console.error("Failed to load dashboard data, falling back to legacy", error);
      // Fallback to legacy method if optimized endpoint fails
      const dashboardParams = {};
      if (libraryId) dashboardParams.library_id = libraryId;

      const [paymentsData, seatsData, dashboardRes] = await Promise.all([
        getPayments({}, forceRefresh),
        getSeatsData(forceRefresh),
        axios.get('/api/dashboard', { params: dashboardParams, skipCache: forceRefresh }).catch(() => ({ data: {} }))
      ]);
      
      return {
        payments: paymentsData,
        seats: seatsData.seats,
        rooms: seatsData.rooms,
        dashboard: dashboardRes.data,
      };
    }
  });
};

export const getSeatsData = async (forceRefresh = false) => {
  if (forceRefresh) recentResponses.delete(scopedCacheKey('getSeatsData'));
  return deduplicateRequest('getSeatsData', async () => {
    try {
        const [seatsRes, roomsRes] = await Promise.all([
            axios.get(`/api/seats`, { skipCache: forceRefresh }),
            axios.get(`/api/rooms`, { skipCache: forceRefresh })
        ]);
        
        return {
            seats: seatsRes.data || [],
            rooms: roomsRes.data || [],
        };
    } catch (error) {
        console.error('Failed to load seats:', error);
        throw error;
    }
  });
};

export const getNotifications = async () => {
  return deduplicateRequest('getNotifications', async () => {
    try {
        const res = await axios.get(`/api/notifications/plans`);
        return res.data.notifications || [];
    } catch (err) {
        console.error("Error loading plan notifications", err);
        throw err;
    }
  });
};

export const getPayments = async (params = {}, forceRefresh = false) => {
  const requestParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
  const cacheKey = `getPayments-${JSON.stringify(requestParams)}`;
  if (forceRefresh) recentResponses.delete(scopedCacheKey(cacheKey));
  return deduplicateRequest(cacheKey, async () => {
    try {
        const res = await axios.get(`/api/payments`, { params: requestParams, skipCache: forceRefresh });
        return res.data || [];
    } catch (error) {
        console.error('Failed to load payments:', error);
        throw error;
    }
  });
};

export const getPaymentsStats = async (libraryId = null, forceRefresh = false) => {
  const cacheKey = `getPaymentsStats-${libraryId}`;
  if (forceRefresh) recentResponses.delete(scopedCacheKey(cacheKey));
  return deduplicateRequest(cacheKey, async () => {
    try {
      const params = {};
      if (libraryId) params.library_id = libraryId;
      const res = await axios.get('/api/payments/stats', { params, skipCache: forceRefresh });
      return res.data || {};
    } catch (error) {
      console.error('Failed to load payment stats:', error);
      throw error;
    }
  });
};

export const getStudentPaymentPlans = async (studentId) => {
    const key = `getStudentPaymentPlans-${studentId}`;
    return deduplicateRequest(key, async () => {
      try {
          const res = await axios.get(`/api/students/${studentId}/payment-plans`);
          return res.data || [];
      } catch (err) {
          console.warn("Specific plan fetch failed, trying fallback...", err);
          const res = await axios.get(`/api/payment-plans`);
          return res.data || [];
      }
    });
};

export const getStudentOutstanding = async (studentId) => {
    const key = `getStudentOutstanding-${studentId}`;
    return deduplicateRequest(key, async () => {
      try {
          const res = await axios.get(`/api/students/${studentId}/outstanding`);
          return res.data || null;
      } catch (err) {
          console.warn('Failed to fetch outstanding info', err);
          return null;
      }
    });
};

export const downloadPaymentReceiptPdf = async (paymentId) => {
  if (!paymentId) {
    throw new Error('Payment ID is required');
  }

  try {
    const response = await axios.get(`/api/payments/${paymentId}/receipt-pdf`, {
      responseType: 'blob',
      timeout: 30000,
    });

    return response.data;
  } catch (error) {
    let message = 'Failed to generate payment receipt PDF';

    // Blob responses need to be decoded before the API error can be shown.
    if (error.response?.data instanceof Blob) {
      try {
        const raw = await error.response.data.text();
        const parsed = JSON.parse(raw);
        message = parsed.error || parsed.message || message;
      } catch (_) {
        // Keep the generic message when the response is not JSON.
      }
    } else {
      message = error.response?.data?.error || error.message || message;
    }

    console.error('❌ Receipt PDF download failed:', error);
    throw new Error(message);
  }
};

// Pull the file name the backend put in Content-Disposition so the browser
// never falls back to a `blob:http://...` name.
const receiptFilenameFromHeaders = (headers, fallback) => {
  const disposition = headers?.['content-disposition'] || '';

  if (!disposition) return fallback;

  const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch (_) {
      // Fall through to the plain filename parameter.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : fallback;
};

export const downloadCombinedPaymentReceiptPdf = async (paymentId) => {
  if (!paymentId) {
    throw new Error('Payment ID is required');
  }

  try {
    const response = await axios.get(`/api/payments/${paymentId}/combined-receipt-pdf`, {
      responseType: 'blob',
      timeout: 60000,
    });

    return {
      blob: response.data,
      filename: receiptFilenameFromHeaders(response.headers, null),
    };
  } catch (error) {
    let message = 'Failed to generate combined payment receipt PDF';

    if (error.response?.data instanceof Blob) {
      try {
        const raw = await error.response.data.text();
        const parsed = JSON.parse(raw);
        message = parsed.error || parsed.message || message;
      } catch (_) {
        // Keep the generic message when the response is not JSON.
      }
    } else {
      message = error.response?.data?.error || error.message || message;
    }

    console.error('❌ Combined receipt PDF download failed:', error);
    throw new Error(message);
  }
};

export const createPayment = async (payload) => {
    try {
        const res = await axios.post(`/api/payments`, payload);
        clearServiceApiCache();
        return res.data;
    } catch (error) {
        console.error('❌ Payment failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const updatePayment = async (paymentId, payload) => {
  try {
    const res = await axios.put(`/api/payments/${paymentId}`, payload);
    clearServiceApiCache();
    return res.data;
  } catch (error) {
    console.error('❌ Payment update failed:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const createStudent = async (payload) => {
    try {
        const res = await axios.post('/api/students', payload);
        clearServiceApiCache();
        return res.data;
    } catch (error) {
        console.error('❌ Student creation failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const updateStudent = async (studentId, payload) => {
    try {
        const res = await axios.put(`/api/students/${studentId}`, payload);
        clearServiceApiCache();
        return res.data;
    } catch (error) {
        console.error('❌ Student update failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const assignSeat = async (studentId, seatId) => {
    try {
        const res = await axios.post(`/api/students/${studentId}/assign-seat-manual`, { seatid: seatId });
        clearServiceApiCache();
        return res.data;
    } catch (error) {
        console.error('❌ Seat assignment failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const assignSeatWithStatus = async (studentId, seatId, status = 'occupied') => {
    try {
        const res = await axios.post(`/api/students/${studentId}/assign-seat-manual`, {
            seatid: seatId,
            status,
        });
        clearServiceApiCache();
        return res.data;
    } catch (error) {
        console.error('❌ Seat assignment failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const getSeatUpgradeQuote = async (studentId) => {
    try {
        const res = await axios.get(`/api/students/${studentId}/seat-upgrade`);
        return res.data || null;
    } catch (error) {
        if (error.response?.status === 404) return null;
        console.error('❌ Seat upgrade quote failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const requestSeatUpgrade = async (studentId, newCategory, paymentData = null) => {
    try {
        const res = await axios.post(`/api/students/${studentId}/upgrade-seat`, {
            new_category: newCategory,
            payment_data: paymentData,
        });
        clearServiceApiCache();
        return res.data || {};
    } catch (error) {
        console.error('❌ Seat upgrade request failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const confirmSeatUpgrade = async (studentId, upgradeId, paymentMode = 'pay_later') => {
    try {
        const res = await axios.post(`/api/students/${studentId}/upgrade-seat`, {
            action: 'confirm',
            upgrade_id: upgradeId,
            payment_mode: paymentMode,
        });
        clearServiceApiCache();
        return res.data || {};
    } catch (error) {
        console.error('❌ Seat upgrade confirmation failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const createSeat = async (payload) => {
  try {
    const res = await axios.post(`/api/seats`, payload);
    clearServiceApiCache();
    return res.data;
  } catch (error) {
    console.error('❌ Seat creation failed:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const sendPlanAlert = async (studentId) => {
  try {
    const res = await axios.post(`/api/students/${studentId}/send-plan-alert`);
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send plan alert:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const sendWhatsappPlanAlert = async (studentId) => {
  try {
    const res = await axios.post(`/api/students/${studentId}/send-whatsapp-plan-alert`);
    return res.data;
  } catch (error) {
    console.error('❌ Failed to send WhatsApp plan alert:', error.response?.data || error);
    throw error.response?.data || error;
  }
};

export const deleteStudent = async (studentId) => {
    try {
        const res = await axios.delete(`/api/students/${studentId}`);
        clearServiceApiCache();
        return res.data;
    } catch (error) {
        console.error('❌ Student deletion failed:', error.response?.data || error);
        throw error.response?.data || error;
    }
};

export const getLibraries = async () => {
    return deduplicateRequest('getLibraries', async () => {
        try {
            const res = await axios.get('/api/libraries');
            return res.data || [];
        } catch (error) {
            console.error('Error fetching libraries:', error);
            return [];
        }
    });
};

export const getPaymentPlans = async () => {
    return deduplicateRequest('getPaymentPlans', async () => {
        try {
            const tenantInfo = JSON.parse(localStorage.getItem('tenantinfo') || '{}');
            const tenantId = tenantInfo.tenant_id || tenantInfo.id || tenantInfo.tenantId;
            if (!tenantId) return [];
            
            const res = await axios.get('/api/payment-plans', { 
                headers: { 'X-Tenant-ID': tenantId.toString() } 
            });
            return res.data || [];
        } catch (err) {
            console.warn('Failed to fetch payment plans:', err);
            return [];
        }
    });
};

export const getStudentsWithOutstanding = async () => {
  return deduplicateRequest('getStudentsWithOutstanding', async () => {
    try {
      const res = await axios.get('/api/dashboard');
      return res.data?.dashboard?.students_with_due || [];
    } catch (error) {
      console.error('❌ Failed to fetch students with outstanding payments:', error);
      return [];
    }
  });
};

export const getReport = async (reportType, params) => {
  const key = `getReport-${reportType}-${JSON.stringify(params)}`;
  return deduplicateRequest(key, async () => {
    try {
      const response = await axios.get(`/api/reports/${reportType}`, { params });
      return response.data;
    } catch (err) {
      console.error(`Failed to load report ${reportType}`, err);
      throw err.response?.data || err;
    }
  });
};

export const exportReport = async (reportType, params) => {
  try {
    const response = await axios.get(`/api/reports/${reportType}/export`, {
      params,
      responseType: 'blob'
    });
    return response.data;
  } catch (err) {
    console.error(`Failed to export report ${reportType}`, err);
    throw err.response?.data || err;
  }
};

export const checkAuth = async () => {
  return deduplicateRequest('checkAuth', async () => {
    try {
      const res = await axios.get('/api/auth/check');
      return res.data;
    } catch (error) {
      console.error('Error checking auth:', error);
      return null;
    }
  });
};

export const getStudentsSummary = async (libraryId = null, forceRefresh = false) => {
  const requestKey = `getStudentsSummary-${libraryId}`;
  if (forceRefresh) recentResponses.delete(scopedCacheKey(requestKey));
  return deduplicateRequest(requestKey, async () => {
    try {
      const params = {};
      if (libraryId) params.library_id = libraryId;

      const res = await axios.get('/api/students/summary', { params, skipCache: forceRefresh });
      return res.data || [];
    } catch (error) {
      console.error('Failed to fetch students summary:', error);
      // Fallback to legacy method
      return getStudents(null, libraryId);
    }
  });
};

export const getAllStudentsData = async (roomId = null, libraryId = null, forceRefresh = false) => {
  const requestKey = `getAllStudentsData-${roomId}-${libraryId}`;
  if (forceRefresh) {
    recentResponses.delete(scopedCacheKey(requestKey));
    recentResponses.delete(scopedCacheKey(`getStudentsSummary-${libraryId}`));
    recentResponses.delete(scopedCacheKey('getSeatsData'));
    recentResponses.delete(scopedCacheKey('getLibraries'));
    recentResponses.delete(scopedCacheKey('getPaymentPlans'));
    recentResponses.delete(scopedCacheKey(`getDashboardData-${libraryId}`));
  }
  return deduplicateRequest(requestKey, async () => {
    try {
      // Use optimized consolidated endpoints
      const [
        studentsSummary,
        seatsData,
        librariesData,
        paymentPlansData,
        dashboardData
      ] = await Promise.all([
        getStudentsSummary(libraryId, forceRefresh),
        getSeatsData(),
        getLibraries(),
        getPaymentPlans(),
        getDashboardData(libraryId, forceRefresh)
      ]);

      return {
        seats: seatsData.seats || [],
        rooms: seatsData.rooms || [],
        students: studentsSummary || [],
        libraries: librariesData || [],
        payments: [], // Included in students summary
        paymentPlans: paymentPlansData || [],
        dashboard: dashboardData?.dashboard || {},
        studentsWithDue: dashboardData?.dashboard?.students_with_due || []
      };
    } catch (error) {
      console.error('Failed to fetch all students data, falling back to legacy:', error);
      // Fallback to legacy method
      const [
        seatsData,
        studentsData,
        librariesData,
        paymentsData,
        paymentPlansData,
        dashboardData
      ] = await Promise.all([
        getSeatsData(),
        getStudents(roomId, libraryId),
        getLibraries(),
        getPayments(),
        getPaymentPlans(),
        getDashboardData(libraryId)
      ]);

      return {
        seats: seatsData.seats || [],
        rooms: seatsData.rooms || [],
        students: studentsData || [],
        libraries: librariesData || [],
        payments: paymentsData || [],
        paymentPlans: paymentPlansData || [],
        dashboard: dashboardData?.dashboard || {},
        studentsWithDue: dashboardData?.dashboard?.students_with_due || []
      };
    }
  });
};